import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import MessagingManagerModal from '../components/MessagingManagerModal';
import { DEFAULT_MESSAGE_TEMPLATE, MSG_PLACEHOLDERS } from '../components/messagingTemplates';
import { AgencyGHLCard } from '../components/integrations/AgencyGHLCard';
import {
  Link2,
  Search,
  CheckCircle,
  XCircle,
  ExternalLink,
  Settings,
  Trash2,
  Plus,
  X,
  Key,
  AlertCircle,
  Zap,
  ChevronDown,
  CreditCard,
  RefreshCw,
  Database,
  MessagesSquare,
  Slack,
  ClipboardList,
  Bot,
  Lock,
  MapPin,
} from 'lucide-react';

// Integration definitions
const INTEGRATIONS = [
  {
    id: 'gohighlevel_agency', name: 'GoHighLevel — Agency Leads',
    description: 'Agency prospecting destination', category: 'leads', agencyGhl: true,
    icon: Link2, color: '#8dbfe8',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT Models & Whisper',
    category: 'ai',
    icon: Zap,
    color: '#10A37F',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-...' }
    ],
    docsUrl: 'https://platform.openai.com/docs',
    hint: 'Bring Your Own Key — sub-accounts with BYOK won\'t be charged credits for chat'
  },
  {
    id: 'anthropic',
    name: 'Claude (Anthropic)',
    description: 'AI chat & Leads generation',
    category: 'ai',
    icon: Zap,
    color: '#D97757',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-ant-...' }
    ],
    docsUrl: 'https://docs.anthropic.com',
    hint: 'Connect once for AI chat and Leads & Outreach. Provider usage and platform fees are billed separately.'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment Processing',
    category: 'payments',
    icon: CreditCard,
    color: '#635BFF',
    fields: [
      { key: 'api_key', label: 'Secret Key', type: 'password', placeholder: 'sk_live_...' }
    ],
    docsUrl: 'https://stripe.com/docs'
  },
  {
    id: 'google_places',
    name: 'Google Places',
    description: 'Unlimited lead searches for Leads & Outreach',
    category: 'leads',
    icon: MapPin,
    color: '#4285F4',
    fields: [
      { key: 'google_api_key', label: 'Google Places API Key', type: 'password', placeholder: 'AIza...' }
    ],
    docsUrl: 'https://console.cloud.google.com/apis/library/places.googleapis.com',
    hint: 'Free from Google (5,000 searches/month on their free tier). In Google Cloud Console: enable "Places API (New)" → Credentials → Create API key. Connecting your own key removes the platform search cap in Leads & Outreach. The key is validated live before saving.'
  },
  {
    id: 'pumble',
    name: 'Pumble',
    description: 'Team messaging & client channels',
    category: 'messaging',
    icon: MessagesSquare,
    color: '#22c1c3',
    messaging: true,
    fields: [
      { key: 'webhook_url', label: 'Incoming Webhook URL', type: 'text', placeholder: 'https://api.pumble.com/workspaces/.../incomingWebhooks/postMessage/...' }
    ],
    docsUrl: 'https://pumble.com/help/integrations/add-pumble-apps/incoming-webhooks-app/',
    hint: 'Fires instantly when a client completes your onboarding form. In Pumble: Apps → Incoming Webhooks → pick a channel → copy the URL.'
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Channel notifications & updates',
    category: 'messaging',
    icon: Slack,
    color: '#E01E5A',
    messaging: true,
    fields: [
      { key: 'webhook_url', label: 'Incoming Webhook URL', type: 'text', placeholder: 'https://hooks.slack.com/services/...' }
    ],
    docsUrl: 'https://api.slack.com/messaging/webhooks',
    hint: 'Fires instantly when a client completes your onboarding form. In Slack: create an app → Incoming Webhooks → pick a channel → copy the URL.'
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Send onboarding data to 7,000+ apps',
    category: 'messaging',
    icon: Zap,
    color: '#FF4F00',
    messaging: true,
    zapier: true,
    fields: [
      { key: 'webhook_url', label: 'Catch Hook URL', type: 'text', placeholder: 'https://hooks.zapier.com/hooks/catch/...' }
    ],
    docsUrl: 'https://zapier.com/apps/webhook/integrations',
    hint: 'Fires instantly when a client completes your onboarding form. In Zapier: create a Zap → trigger "Webhooks by Zapier" → Catch Hook → copy the URL.'
  }
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'ai', label: 'AI Models' },
  { id: 'payments', label: 'Payments' },
  { id: 'leads', label: 'Leads' },
  { id: 'messaging', label: 'Messaging' }
];

const IntegrationsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  // BYOK status from the new encrypted endpoints — separate from the
  // generic `integrations` collection because OpenAI/Anthropic keys live
  // on `agencies.byok_keys.*` (encrypted at rest, tenant-scoped).
  const [byokStatus, setByokStatus] = useState({});
  // Set of provider IDs ('openai' / 'anthropic') used by any agent in
  // the workspace — used to surface the "you only connected OpenAI but
  // your agents use Claude" warning.
  const [providersInUse, setProvidersInUse] = useState(new Set());
  const [messagingIntegrations, setMessagingIntegrations] = useState([]);
  const [leadsSettings, setLeadsSettings] = useState(null);
  const [agencyGhlStatus, setAgencyGhlStatus] = useState(null);
  
  // Modal states
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [formData, setFormData] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [msgTesting, setMsgTesting] = useState(false);
  const [msgTestResult, setMsgTestResult] = useState(null);
  const [showMsgFields, setShowMsgFields] = useState(false);
  const [msgManager, setMsgManager] = useState(null);

  useEffect(() => {
    if (workspaceId) {
      fetchData();
    }
  }, [workspaceId]);

  const fetchData = async () => {
    try {
      const [wsResponse, intResponse, byokResponse, agentsResponse, msgResponse, leadsResponse] = await Promise.all([
        api.get(`/api/workspaces/${workspaceId}`),
        api.get(`/api/workspaces/${workspaceId}/integrations`).catch(() => ({ data: [] })),
        // BYOK status — last4, validated_at, health per provider.
        // Tenant-scoped on the backend by workspace_id + auth.
        api.get(`/api/workspaces/${workspaceId}/byok`).catch(() => ({ data: {} })),
        // What models do agents in this workspace actually use? Drives
        // the partial-coverage warning ("you connected OpenAI but
        // agent X uses claude-sonnet").
        api.get(`/api/workspaces/${workspaceId}/agents`).catch(() => ({ data: [] })),
        api.get(`/api/workspaces/${workspaceId}/integrations/messaging`).catch(() => ({ data: { integrations: [] } })),
        api.get(`/api/workspaces/${workspaceId}/leads/settings`).catch(() => ({ data: null })),
      ]);
      setWorkspace(wsResponse.data);
      setConnectedIntegrations(intResponse.data || []);
      setByokStatus(byokResponse.data || {});
      // Derive providers in use from agents' ai_model fields.
      const inUse = new Set();
      for (const a of (agentsResponse.data || [])) {
        const m = (a.ai_model || a.chat_model || '').toLowerCase();
        if (m.startsWith('gpt-')) inUse.add('openai');
        else if (m.startsWith('claude-')) inUse.add('anthropic');
      }
      setProvidersInUse(inUse);
      setMessagingIntegrations(msgResponse.data?.integrations || []);
      setLeadsSettings(leadsResponse.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConnect = (integration) => {
    setSelectedIntegration(integration);
    if (integration.messaging) {
      const existing = messagingIntegrations.find(m => m.provider === integration.id) || {};
      setFormData({
        webhook_url: existing.webhook_url || '',
        message_template: existing.message_template || DEFAULT_MESSAGE_TEMPLATE,
      });
    } else {
      setFormData({});
    }
    setMsgTestResult(null);
    setShowMsgFields(false);
    setShowConnectModal(true);
  };

  const sendMsgTest = async () => {
    if (!(formData.webhook_url || '').trim()) {
      toast.error('Paste your webhook URL first');
      return;
    }
    setMsgTesting(true);
    setMsgTestResult(null);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/integrations/messaging/test`, {
        provider: selectedIntegration.id,
        webhook_url: formData.webhook_url || '',
        message_template: selectedIntegration.zapier ? '' : (formData.message_template || ''),
      });
      setMsgTestResult(res.data);
      if (res.data.ok) toast.success(`Test ${selectedIntegration.zapier ? 'data' : 'message'} sent — check ${selectedIntegration.zapier ? 'your Zap history' : 'your channel'}!`);
      else toast.error(`Webhook responded with ${res.data.status_code || 'an error'}`);
    } catch (e) {
      setMsgTestResult({ ok: false, error: e.response?.data?.detail || 'Request failed' });
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setMsgTesting(false);
    }
  };

  const copyPlaceholder = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`);
    toast.success(`{{${key}}} copied — paste it into your template`);
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    
    try {
      // BYOK providers (OpenAI / Anthropic) route through the new
      // encrypted, validated, tenant-scoped endpoint. Live key
      // validation happens server-side — invalid keys are rejected
      // before storage so users see the error inline.
      if (selectedIntegration.messaging) {
        await api.put(`/api/workspaces/${workspaceId}/integrations/messaging`, {
          provider: selectedIntegration.id,
          webhook_url: formData.webhook_url || '',
          message_template: selectedIntegration.zapier ? '' : (formData.message_template || ''),
          enabled: true,
        });
        toast.success(`${selectedIntegration.name} connected — onboarding notifications are live`);
      } else if (selectedIntegration.id === 'openai' || selectedIntegration.id === 'anthropic') {
        await api.put(`/api/workspaces/${workspaceId}/byok`, {
          provider: selectedIntegration.id,
          api_key: formData.api_key,
        });
        toast.success(`${selectedIntegration.name} key validated & saved (encrypted)`);
      } else if (selectedIntegration.id === 'google_places') {
        await api.put(`/api/workspaces/${workspaceId}/leads/settings`, {
          google_api_key: formData.google_api_key || '',
        });
        toast.success('Google Places key validated & saved — unlimited lead searches unlocked');
      } else {
        // Other integrations (Stripe, etc.) still use the generic store.
        await api.post(`/api/workspaces/${workspaceId}/integrations`, {
          integration_id: selectedIntegration.id,
          integration_name: selectedIntegration.name,
          credentials: formData,
        });
        toast.success(`${selectedIntegration.name} connected successfully`);
      }
      setShowConnectModal(false);
      setSelectedIntegration(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect integration');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!window.confirm('Are you sure you want to disconnect this integration?')) return;
    
    try {
      if (integrationId === 'openai' || integrationId === 'anthropic') {
        await api.delete(`/api/workspaces/${workspaceId}/byok/${integrationId}`);
      } else if (integrationId === 'google_places') {
        await api.put(`/api/workspaces/${workspaceId}/leads/settings`, { google_api_key: '' });
      } else {
        await api.delete(`/api/workspaces/${workspaceId}/integrations/${integrationId}`);
      }
      toast.success('Integration disconnected');
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect integration');
    }
  };

  const isConnected = (integrationId) => {
    // BYOK providers: use the encrypted store.
    if (integrationId === 'openai' || integrationId === 'anthropic') {
      return !!(byokStatus[integrationId] && byokStatus[integrationId].connected);
    }
    if (integrationId === 'google_places') {
      return !!leadsSettings?.has_own_key;
    }
    const integration = INTEGRATIONS.find(i => i.id === integrationId);
    if (integration?.messaging) {
      return messagingIntegrations.some(m => m.provider === integrationId && m.enabled && m.webhook_url);
    }
    return connectedIntegrations.some(c => c.integration_id === integrationId);
  };

  const getConnectionStatus = (integrationId) => {
    if (integrationId === 'openai' || integrationId === 'anthropic') {
      const b = byokStatus[integrationId];
      if (b && b.connected) {
        return {
          integration_id: integrationId,
          status: b.health === 'invalid' ? 'error' : 'active',
          last4: b.last4,
          validated_at: b.validated_at,
          health: b.health,
        };
      }
      return null;
    }
    if (integrationId === 'google_places') {
      if (leadsSettings?.has_own_key) {
        return { integration_id: 'google_places', status: 'active', last4: leadsSettings.key_preview };
      }
      return null;
    }
    return connectedIntegrations.find(c => c.integration_id === integrationId);
  };

  // Partial-coverage warning: providers an agent USES but you haven't
  // connected a BYOK key for. Those chats fall through to the platform
  // key and get billed at the standard Voxinity chat rate.
  const partialCoverageProviders = Array.from(providersInUse).filter(
    (p) => !isConnected(p),
  );

  // Plan gating — BYOK requires Starter+. Locked cards stay VISIBLE with
  // an upgrade prompt (backend enforces too).
  const effectivePlan = (() => {
    const p = workspace?.plan || 'launchpad';
    if (p === 'lifetime') return workspace?.base_plan || 'pro';
    if (p === 'free') return 'launchpad';
    return p;
  })();
  const requiredPlanFor = (integrationId) => {
    if (integrationId === 'openai' || integrationId === 'anthropic') {
      return effectivePlan === 'launchpad' ? 'Starter' : null;
    }
    return null;
  };

  const filteredIntegrations = INTEGRATIONS.filter(int => {
    const matchesSearch = int.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         int.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || int.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="integrations-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-white mb-1">
                Integrations
              </h1>
              <p className="text-gray-400">Connect your favorite tools and services</p>
            </div>
          </div>
        </motion.div>

        {/* BYOK Information Panel — moved BELOW Available Integrations
            per user feedback. Simplified copy. Now also covers how
            Stripe powers re-billing margin payouts. The dynamic
            partial-coverage warning still surfaces here. */}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <Link2 className="text-teal-400" size={20} />
              <div>
                <p data-testid="integrations-connected-count" className="text-2xl font-semibold text-white">{connectedIntegrations.length + (agencyGhlStatus?.has_connection ? 1 : 0)}</p>
                <p className="text-xs text-gray-400">Connected</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">
                  {connectedIntegrations.filter(c => c.status === 'active').length + (agencyGhlStatus?.health === 'connected' ? 1 : 0)}
                </p>
                <p className="text-xs text-gray-400">Active</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="text-yellow-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">
                  {connectedIntegrations.filter(c => c.status === 'error').length + (agencyGhlStatus?.has_connection && !['connected', 'not_checked'].includes(agencyGhlStatus.health) ? 1 : 0)}
                </p>
                <p className="text-xs text-gray-400">Needs Attention</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <Database className="text-violet-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">{INTEGRATIONS.length}</p>
                <p className="text-xs text-gray-400">Available</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-xl p-4 mb-6"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
                data-testid="integration-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    categoryFilter === cat.id
                      ? 'bg-teal-400/20 text-teal-400 border border-teal-400/30'
                      : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Info Box about Agency-Level Keys */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-blue-400/5 border border-blue-400/20 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
            <div>
              <h3 className="font-medium text-white text-sm mb-1">Agency-Level API Keys</h3>
              <p className="text-xs text-gray-400">
                API keys configured here are shared across all your sub-accounts as fallbacks. 
                Sub-accounts can override these with their own keys for separate billing. 
                <span className="text-blue-400 ml-1" data-testid="agency-ghl-isolation-note">The Agency Leads GHL destination is separate. Client LeadConnector connections remain managed in their sub-account settings.</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Connected Integrations */}
        {connectedIntegrations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Connected</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connectedIntegrations.map((connection) => {
                const integration = INTEGRATIONS.find(i => i.id === connection.integration_id);
                if (!integration) return null;
                const Icon = integration.icon;
                
                return (
                  <div
                    key={connection.integration_id}
                    className="glass-card rounded-xl p-4 border border-green-400/20"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${integration.color}20` }}
                        >
                          <Icon size={20} style={{ color: integration.color }} />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-white">{integration.name}</h3>
                          <span className={`text-xs flex items-center gap-1 ${
                            connection.status === 'active' ? 'text-green-400' : 'text-yellow-400'
                          }`}>
                            {connection.status === 'active' ? (
                              <><CheckCircle size={12} /> Connected</>
                            ) : (
                              <><AlertCircle size={12} /> Issue detected</>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                          title="Settings"
                        >
                          <Settings size={14} />
                        </button>
                        <button
                          onClick={() => handleDisconnect(connection.integration_id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                          title="Disconnect"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Connected {new Date(connection.connected_at).toLocaleDateString()}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Available Integrations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-lg font-semibold text-white mb-4">Available Integrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredIntegrations.map((integration) => {
              if (integration.agencyGhl) return <AgencyGHLCard key={integration.id} api={api} workspaceId={workspaceId} onStatusChange={setAgencyGhlStatus}/>;
              const Icon = integration.icon;
              const connected = isConnected(integration.id);
              const lockedPlan = !connected ? requiredPlanFor(integration.id) : null;
              
              return (
                <motion.div
                  key={integration.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`relative glass-card rounded-xl p-5 transition-all duration-300 ${
                    connected
                      ? 'border border-green-400/40 shadow-[0_0_24px_-6px_rgba(74,222,128,0.45)] hover:shadow-[0_0_32px_-4px_rgba(74,222,128,0.6)] hover:border-green-400/60 bg-gradient-to-br from-green-400/[0.04] to-transparent'
                      : 'hover:border-white/20'
                  }`}
                  data-testid={`integration-${integration.id}`}
                >
                  {connected && (
                    <span className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-green-400/20" aria-hidden="true" />
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        connected ? 'ring-2 ring-green-400/30' : ''
                      }`}
                      style={{
                        backgroundColor: connected ? `${integration.color}30` : `${integration.color}15`,
                        boxShadow: connected ? `0 0 18px -4px ${integration.color}80` : 'none',
                      }}
                    >
                      <Icon size={24} style={{ color: integration.color }} />
                    </div>
                    {connected && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-green-300 bg-green-400/15 border border-green-400/40 px-2 py-0.5 rounded-full"
                        data-testid={`connected-badge-${integration.id}`}
                      >
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-400" />
                        </span>
                        Connected
                      </span>
                    )}
                    {!connected && lockedPlan && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-300 bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 rounded-full"
                        data-testid={`plan-required-badge-${integration.id}`}
                      >
                        <Lock size={10} />
                        {lockedPlan}+ plan
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-base font-medium text-white mb-1">{integration.name}</h3>
                  <p className="text-sm text-gray-400 mb-4">{integration.description}</p>
                  
                  <div className="flex items-center gap-2">
                    {integration.messaging ? (
                      <button
                        onClick={() => setMsgManager(integration)}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          connected
                            ? 'bg-green-400/10 text-green-300 border border-green-400/30 hover:bg-green-400/20'
                            : 'bg-teal-400/10 text-teal-400 hover:bg-teal-400/20'
                        }`}
                        data-testid={`manage-${integration.id}`}
                      >
                        <Settings size={14} />
                        Manage
                        {messagingIntegrations.filter(m => m.provider === integration.id).length > 0 && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-white/10">
                            {messagingIntegrations.filter(m => m.provider === integration.id).length}
                          </span>
                        )}
                      </button>
                    ) : connected ? (
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-400/10 text-green-300 text-sm border border-green-400/30 hover:bg-red-400/10 hover:text-red-300 hover:border-red-400/30 transition-colors group"
                        data-testid={`disconnect-${integration.id}`}
                      >
                        <CheckCircle size={14} className="group-hover:hidden" />
                        <Trash2 size={14} className="hidden group-hover:inline" />
                        <span className="group-hover:hidden">Connected</span>
                        <span className="hidden group-hover:inline">Disconnect</span>
                      </button>
                    ) : lockedPlan ? (
                      <button
                        onClick={() => navigate(`/workspace/${workspaceId}/billing`)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-400/10 text-amber-300 text-sm border border-amber-400/30 hover:bg-amber-400/20 transition-colors"
                        data-testid={`upgrade-${integration.id}`}
                      >
                        <Lock size={14} />
                        Upgrade to unlock
                      </button>
                    ) : (
                      <button
                        onClick={() => handleOpenConnect(integration)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-teal-400/10 text-teal-400 text-sm hover:bg-teal-400/20 transition-colors"
                        data-testid={`connect-${integration.id}`}
                      >
                        <Plus size={14} />
                        Connect
                      </button>
                    )}
                    <a
                      href={integration.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      title="View Documentation"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
          
          {filteredIntegrations.length === 0 && (
            <div className="text-center py-12">
              <Search className="mx-auto text-gray-600 mb-3" size={32} />
              <p className="text-gray-400">No integrations found matching your search</p>
            </div>
          )}
        </motion.div>

        {/* How billing works — moved below Available Integrations per
            user feedback. Simplified: 3 short cards instead of the
            previous bullet list. Now also explains how Stripe powers
            sub-account re-billing margin payouts. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
          data-testid="byok-info-panel"
        >
          <h2 className="text-lg font-semibold text-white mb-3">How billing works</h2>

          {/* Three side-by-side cards — AI BYOK, Stripe re-billing,
              and the partial-coverage rule. Mobile stacks to one column. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            {/* Card 1: AI provider keys */}
            <div className="glass-card rounded-xl p-4 border-l-2 border-teal-400/60">
              <div className="flex items-center gap-2 mb-2">
                <Key className="text-teal-400 shrink-0" size={16} />
                <h3 className="text-sm font-semibold text-white">AI provider keys (OpenAI / Claude)</h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Connect a key → those chats bill your provider account directly. Voxinity adds a <strong className="text-amber-300">$0.001/msg</strong> routing fee (vs <strong>$0.025/msg</strong> standard — <strong>96% cheaper</strong>).
              </p>
            </div>

            {/* Card 2: Stripe re-billing */}
            <div className="glass-card rounded-xl p-4 border-l-2 border-indigo-400/60">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="text-indigo-400 shrink-0" size={16} />
                <h3 className="text-sm font-semibold text-white">Stripe (re-billing)</h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Connect Stripe → your sub-accounts top up their own balance at <strong>your markup rate</strong>. Voxinity takes its share, your margin is auto-credited — no manual payouts.
              </p>
            </div>

            {/* Card 3: Partial-coverage rule */}
            <div className="glass-card rounded-xl p-4 border-l-2 border-amber-400/60">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="text-amber-400 shrink-0" size={16} />
                <h3 className="text-sm font-semibold text-white">Heads up — partial coverage</h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Connect only OpenAI? Agents on Claude models still bill at our standard rate (and vice-versa). Connect both providers for full BYOK coverage.
              </p>
            </div>
          </div>

          {/* Dynamic partial-coverage warning — surfaces only when the
              workspace's agents actually use a provider that has no
              BYOK key connected. */}
          {partialCoverageProviders.length > 0 && (
            <div className="rounded-xl p-3 bg-amber-500/10 border border-amber-400/30">
              <p className="text-sm text-amber-200" data-testid="byok-partial-coverage-warning">
                <AlertCircle className="inline mr-1.5 -mt-0.5" size={14} />
                <strong>Partial coverage detected:</strong> agents here use <strong>{partialCoverageProviders.map(p => p === 'openai' ? 'OpenAI' : 'Anthropic').join(', ')}</strong> models but no BYOK key is connected for that provider. Those chats bill at the standard Voxinity rate.
              </p>
            </div>
          )}
        </motion.div>

        {/* How channel messaging works — Pumble / Slack / Zapier.
            Mirrors the "How billing works" 3-card layout. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
          data-testid="messaging-info-panel"
        >
          <h2 className="text-lg font-semibold text-white mb-3">How Pumble, Slack &amp; Zapier work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Card 1: Onboarding alerts */}
            <div className="glass-card rounded-xl p-4 border-l-2 border-teal-400/60">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="text-teal-400 shrink-0" size={16} />
                <h3 className="text-sm font-semibold text-white">Client Onboarding alerts</h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Every completed onboarding form fires an instant alert to each connected channel — a formatted message in <strong>Pumble/Slack</strong>, or structured JSON to <strong>Zapier</strong> for 7,000+ apps. Create as many channels as you need, each with its own message template.
              </p>
            </div>

            {/* Card 3: Trigger pricing */}
            <div className="glass-card rounded-xl p-4 border-l-2 border-amber-400/60">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="text-amber-400 shrink-0" size={16} />
                <h3 className="text-sm font-semibold text-white">Trigger pricing</h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Each delivered message = <strong className="text-amber-300">$0.01 per trigger</strong>, billed to your workspace balance and itemized in your transaction history. Test messages are always free — you only pay when a channel actually fires.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Connect Modal */}
      <AnimatePresence>
        {showConnectModal && selectedIntegration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowConnectModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full ${selectedIntegration.messaging ? 'max-w-2xl' : 'max-w-md'} bg-space-800 rounded-xl overflow-hidden max-h-[92vh] flex flex-col`}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${selectedIntegration.color}15` }}
                    >
                      <selectedIntegration.icon size={20} style={{ color: selectedIntegration.color }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Connect {selectedIntegration.name}</h3>
                      <p className="text-sm text-gray-400">{selectedIntegration.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowConnectModal(false)}
                    className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleConnect} className="p-6 overflow-y-auto">
                {selectedIntegration.oauth ? (
                  <div className="text-center py-6">
                    <Key className="mx-auto text-gray-400 mb-4" size={32} />
                    <p className="text-gray-300 mb-4">
                      Click below to authenticate with {selectedIntegration.name}
                    </p>
                    <button
                      type="button"
                      className="px-6 py-3 rounded-lg text-white font-medium transition-colors"
                      style={{ backgroundColor: selectedIntegration.color }}
                    >
                      Connect with {selectedIntegration.name}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedIntegration.hint && (
                      <div className="p-3 rounded-lg bg-blue-400/10 border border-blue-400/20 mb-4">
                        <p className="text-xs text-blue-400">{selectedIntegration.hint}</p>
                      </div>
                    )}
                    {selectedIntegration.fields?.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm text-gray-400 mb-2">{field.label}</label>
                        <input
                          type={field.type}
                          value={formData[field.key] || ''}
                          onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
                          required={field.required !== false}
                          data-testid={`field-${field.key}`}
                        />
                      </div>
                    ))}

                    {/* Messaging: customizable template (Pumble / Slack) */}
                    {selectedIntegration.messaging && !selectedIntegration.zapier && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm text-gray-400">Message template</label>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, message_template: DEFAULT_MESSAGE_TEMPLATE })}
                            className="text-xs text-teal-400 hover:underline"
                            data-testid="msg-template-reset"
                          >
                            Reset to default
                          </button>
                        </div>
                        <textarea
                          rows={10}
                          value={formData.message_template || ''}
                          onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50 text-xs font-mono leading-relaxed"
                          data-testid="msg-template-input"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Used for the 🔥 New Client Onboarded alert — other alerts use built-in messages. Placeholders like {'{{business_legal_name}}'} are replaced with the client's answers. Empty fields show as "—".</p>
                      </div>
                    )}

                    {/* Zapier: structured payload note */}
                    {selectedIntegration.zapier && (
                      <div className="p-3 rounded-lg bg-orange-400/10 border border-orange-400/20">
                        <p className="text-xs text-orange-300">
                          Zapier receives <strong>all onboarding fields as structured JSON</strong> — map them to any action in your Zap (Sheets, CRM, email…). Use "Send Test Data" below, then hit "Test trigger" in Zapier to pull in the sample record.
                        </p>
                      </div>
                    )}

                    {/* Available data fields reference */}
                    {selectedIntegration.messaging && (
                      <div>
                        <button
                          type="button"
                          onClick={() => setShowMsgFields(v => !v)}
                          className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-teal-400 transition-colors"
                          data-testid="msg-toggle-fields"
                        >
                          <ChevronDown size={14} className={`transition-transform ${showMsgFields ? 'rotate-180' : ''}`} />
                          {showMsgFields ? 'Hide' : 'View'} all {MSG_PLACEHOLDERS.length} data fields you can use
                        </button>
                        {showMsgFields && (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="msg-fields-panel">
                            {MSG_PLACEHOLDERS.map(key => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => copyPlaceholder(key)}
                                className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] font-mono text-teal-300 hover:bg-teal-400/10 hover:border-teal-400/30 transition-colors"
                                title="Click to copy"
                                data-testid={`msg-field-${key}`}
                              >
                                {`{{${key}}}`}
                              </button>
                            ))}
                            <p className="w-full text-[11px] text-gray-500 mt-1">Click any field to copy it, then paste it into your template.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Send test */}
                    {selectedIntegration.messaging && (
                      <div className="flex flex-wrap items-center gap-2.5">
                        <button
                          type="button"
                          onClick={sendMsgTest}
                          disabled={msgTesting || !(formData.webhook_url || '').trim()}
                          className="px-4 py-2 rounded-lg bg-sky-400/15 text-sky-400 border border-sky-400/30 text-xs font-semibold hover:bg-sky-400/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          data-testid="msg-send-test"
                        >
                          {msgTesting ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
                          {msgTesting ? 'Sending…' : (selectedIntegration.zapier ? 'Send Test Data' : 'Send Test Message')}
                        </button>
                        {msgTestResult && (
                          msgTestResult.ok ? (
                            <span className="flex items-center gap-1.5 text-xs font-medium text-teal-400 bg-teal-400/10 px-3 py-1.5 rounded-lg" data-testid="msg-test-success">
                              <CheckCircle size={13} /> Delivered ({msgTestResult.status_code}) — check {selectedIntegration.zapier ? 'your Zap' : 'your channel'}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 px-3 py-1.5 rounded-lg" data-testid="msg-test-fail">
                              <XCircle size={13} /> {msgTestResult.error || `Failed (${msgTestResult.status_code})`}
                            </span>
                          )
                        )}
                      </div>
                    )}

                    <div className="p-3 rounded-lg bg-teal-400/10 border border-teal-400/20">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="text-teal-400 flex-shrink-0 mt-0.5" size={16} />
                        <p className="text-xs text-gray-300">
                          Your credentials are encrypted and stored securely. 
                          <a 
                            href={selectedIntegration.docsUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-teal-400 hover:underline ml-1"
                          >
                            View docs
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowConnectModal(false)}
                    className="flex-1 px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={connecting}
                    className="flex-1 px-4 py-3 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-connect-btn"
                  >
                    {connecting ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Plus size={16} />
                    )}
                    {connecting ? 'Connecting...' : 'Connect'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {msgManager && (
        <MessagingManagerModal
          integration={msgManager}
          workspaceId={workspaceId}
          api={api}
          isAdmin={isAdmin}
          items={messagingIntegrations.filter(m => m.provider === msgManager.id)}
          onChanged={fetchData}
          onClose={() => setMsgManager(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default IntegrationsPage;
